import { Group } from 'src/design/atoms/Group';
import { Loader } from 'src/design/atoms/Loader';
import { Stack } from 'src/design/atoms/Stack';
import { Text } from 'src/design/atoms/Text';
import { Table } from 'src/design/molecules/Table';
import React from 'react';
import { TimesheetDto, TimesheetRowDto } from 'src/api/FiretrolApiClient';
import { useJobWbs } from 'src/api/queries/wbs/useJobWbs';
import { EffectiveRateCell } from 'src/app/timesheets/EffectiveRateCell';
import { PayCodeHoursSummary } from 'src/app/timesheets/PayCodeHoursSummary';
import { splitHoursByOvertime } from 'src/app/timesheets/splitHoursByOvertime';
import { ScaleBadge } from 'src/app/timesheets/ScaleBadge';
import { useOrganization } from 'src/hooks/useOrganization';
import { useTimesheetCalculations } from 'src/hooks/useTimesheetCalculations';
import {
  convertAccountsToTreeData,
  formatHours as formatHoursUtil,
  traverseWbsTree,
} from 'src/utils';
import { ChargeCodeCell } from 'src/app/timesheets/ChargeCodeCell';

interface TimesheetDetailPanelProps {
  timesheet: TimesheetDto | null;
}

const formatHours = (hours: number | undefined): string => {
  const value = Number(hours || 0);
  return value === 0 ? '' : value.toFixed(2);
};

export const TimesheetDetailPanel: React.FC<TimesheetDetailPanelProps> = ({
  timesheet,
}) => {
  const { payCodes } = useOrganization();
  const { data: wbsData, isLoading: loading } = useJobWbs(timesheet?.jobId);

  const accountMap = React.useMemo(() => {
    const map = new Map<string, string>();
    if (!wbsData?.wbsAccount) return map;
    traverseWbsTree(convertAccountsToTreeData(wbsData.wbsAccount), (node) => {
      map.set(node.id, node.path || node.name);
    });
    return map;
  }, [wbsData]);

  const { totalHours, hoursByPayCode, totalsByDay, getPayCodeName } =
    useTimesheetCalculations(timesheet?.rows || [], payCodes);

  const isServiceJob = timesheet?.job?.jobType === 'service';

  if (!timesheet?.rows) return null;

  const { regularHours, overtimeHours } = splitHoursByOvertime(timesheet.rows);

  return (
    <Stack gap="sm">
      <Group justify="center" gap="lg">
        <Text size="lg" fw={500} c="bright">
          Hours: {formatHoursUtil(regularHours)}
        </Text>
        <Text size="lg" fw={500} c="bright">
          Overtime: {formatHoursUtil(overtimeHours)}
        </Text>
      </Group>
      <PayCodeHoursSummary hoursByPayCode={hoursByPayCode} />
      <Table miw={750}>
        <Table.Thead>
          <Table.Tr>
            <Table.Th align="center" w="400px">
              Charge Code
            </Table.Th>
            <Table.Th align="center">Pay Code</Table.Th>
            <Table.Th align="center">Rate</Table.Th>
            <Table.Th align="center">Sat</Table.Th>
            <Table.Th align="center">Sun</Table.Th>
            <Table.Th align="center">Mon</Table.Th>
            <Table.Th align="center">Tue</Table.Th>
            <Table.Th align="center">Wed</Table.Th>
            <Table.Th align="center">Thu</Table.Th>
            <Table.Th align="center">Fri</Table.Th>
            <Table.Th align="center">Total</Table.Th>
          </Table.Tr>
        </Table.Thead>
        <Table.Tbody>
          {timesheet.rows.map((row) => (
            <Table.Tr key={row.id}>
              <Table.Td align="right">
                {loading && !isServiceJob ? (
                  <Loader size="xs" />
                ) : (
                  <ChargeCodeCell
                    isServiceJob={isServiceJob}
                    serviceCallId={row.serviceCallId}
                    serviceDescription={row.serviceDescription}
                    serviceLocation={row.serviceLocation}
                    fallback={
                      accountMap.get(row.wbsAccountId ?? '') || 'Unknown'
                    }
                    abbreviate={{ description: 25, location: 15 }}
                  />
                )}
              </Table.Td>
              <Table.Td align="center">
                {getPayCodeName(row.payCodeId)}
              </Table.Td>
              <Table.Td align="center">
                <Group gap="xs" justify="center" wrap="nowrap">
                  <EffectiveRateCell
                    effectiveRate={row.effectiveRate}
                    instructions={row.breakdownInstructions}
                    loading={false}
                    error={null}
                    isActualized={timesheet?.status === 'Approved'}
                    payCodeColor={row.payCode?.color}
                  />
                  <ScaleBadge
                    isScale={row.isScalePay}
                    bypassScalePay={
                      row.bypassScalePay || row.payCode?.bypassScalePay
                    }
                    isDirect={row.payCode?.isDirect}
                    scalePayAdjustmentAmount={row.scalePayAdjustmentAmount}
                  />
                </Group>
              </Table.Td>
              <Table.Td align="center">{formatHours(row.sat)}</Table.Td>
              <Table.Td align="center">{formatHours(row.sun)}</Table.Td>
              <Table.Td align="center">{formatHours(row.mon)}</Table.Td>
              <Table.Td align="center">{formatHours(row.tue)}</Table.Td>
              <Table.Td align="center">{formatHours(row.wed)}</Table.Td>
              <Table.Td align="center">{formatHours(row.thu)}</Table.Td>
              <Table.Td align="center">{formatHours(row.fri)}</Table.Td>
              <Table.Td align="center">{formatHours(row.total)}</Table.Td>
            </Table.Tr>
          ))}
          {timesheet.rows.length > 0 && (
            <Table.Tr>
              <Table.Td align="right">Totals</Table.Td>
              <Table.Td></Table.Td>
              <Table.Td></Table.Td>
              <Table.Td align="center">{formatHours(totalsByDay.sat)}</Table.Td>
              <Table.Td align="center">{formatHours(totalsByDay.sun)}</Table.Td>
              <Table.Td align="center">{formatHours(totalsByDay.mon)}</Table.Td>
              <Table.Td align="center">{formatHours(totalsByDay.tue)}</Table.Td>
              <Table.Td align="center">{formatHours(totalsByDay.wed)}</Table.Td>
              <Table.Td align="center">{formatHours(totalsByDay.thu)}</Table.Td>
              <Table.Td align="center">{formatHours(totalsByDay.fri)}</Table.Td>
              <Table.Td align="center">{formatHours(totalHours)}</Table.Td>
            </Table.Tr>
          )}
        </Table.Tbody>
      </Table>
    </Stack>
  );
};
